import React, { FC, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { type Editor, isNodeSelection } from '@tiptap/core';
import { usePortalNode } from '@wix/da-ds/pkg/popper/hooks/usePortalNode';
import throttle from 'lodash/throttle';
import ddt from '@wix/da-ddt';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { getMenuUpdate } from '../../../redux/selectors/editor';

import s from './BlockBubbleMenu.scss';

export interface BlockBubbleMenuProps {
  editor: Editor;

  /**
   * A selector for a child to be used for size and position
   */
  childSelector?: string;

  /**
   * When to show the menu, usually after something is focused
   *
   * For selection something like !editor.state.selection.empty && editor.isFocused
   */
  open: boolean;

  /**
   * Your menu
   */
  children: React.ReactNode;

  /**
   * The height of menu, used to determine onscreen-ness
   */
  menuHeight: number;

  /**
   * The width of menu, used to determine onscreen-ness
   */
  menuWidth: number;

  /**
   * The offset in pixels from the top of the selection rect
   */
  menuOffset?: number;

  /**
   * The padding to add when moving back onto screen when it goes offscreen
   */
  borderPadding?: number;

  /**
   * Wrapper classname
   */
  className?: string;

  /**
   * The scrollable the editor is within, this is usually not viewport so you generally
   * want this options as we need to account for it when placing the menu.
   */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}

const ContainerPortal = ({ children }) => {
  const portalContainer = usePortalNode('bubblemenublock');
  return portalContainer && ReactDOM.createPortal(children, portalContainer);
};

/**
 *
 */
export const BlockBubbleMenu: FC<BlockBubbleMenuProps> = ({
  editor,
  open,
  className,
  children,
  scrollContainerRef,
  menuOffset = 8,
  menuHeight,
  menuWidth,
  borderPadding = 8,
  childSelector,
}) => {
  const menuUp = useSelector(getMenuUpdate); // used for externals to trigger updates
  const [winScrollPos, setWinScrollPos] = useState<any>({
    y: window.scrollY,
    x: window.scrollX,
  });
  const [innerScrollPos, setInnerScrollPos] = useState<any>({ y: 0, x: 0 });
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({});

  // Track the window position
  useEffect(() => {
    const scroller = window;
    const handleScroll = throttle(() => {
      setWinScrollPos({ y: scroller?.scrollY, x: scroller?.scrollX });
    }, 200);
    if (scroller) {
      scroller.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => scroller?.removeEventListener('scroll', handleScroll);
  }, [open]);

  // Force updates when a non page scroller scrolls, we need to update
  // the menu position on scroll / we don't actually use the value.
  useEffect(() => {
    const scroller = scrollContainerRef?.current;
    const handleScroll = throttle(() => {
      setInnerScrollPos({ y: scroller?.scrollTop, x: scroller?.scrollLeft });
    }, 200);
    if (scroller) {
      scroller.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => scroller?.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef, open]);

  useEffect(() => {
    if (open) {
      // RAF because sometimes the DOM isn't ready in the same render
      requestAnimationFrame(() => {
        const { ranges } = editor.state.selection;
        const from = Math.min(...ranges.map(range => range.$from.pos));

        let rect;
        // Block is selected only
        if (isNodeSelection(editor.state.selection)) {
          const node = editor.view.nodeDOM(from);
          if (node instanceof HTMLElement) {
            if (childSelector) {
              const child = node.querySelector(childSelector);
              if (child) {
                rect = child.getBoundingClientRect();
              }
            } else {
              rect = node.getBoundingClientRect();
            }
          }
        }
        if (rect) {
          let newTop = rect.top + winScrollPos.y - menuOffset - menuHeight;
          let newLeft: string | number =
            rect.left + winScrollPos.x + rect.width / 2 - menuWidth / 2;
          let newRight: string | undefined = undefined;

          // off right of screen we move it back inside
          if (newLeft + menuWidth > (visualViewport?.width ?? Infinity)) {
            newLeft = 'auto';
            newRight = `${borderPadding}px`;
          }

          // off top of scrollarea

          // With ref we keep within the scrollable not the screen
          const scrollableRefRect =
            scrollContainerRef?.current?.getBoundingClientRect();
          if (scrollableRefRect) {
            // the selection rect top is outside the scrollarea and will be floating
            // in the page, we bring it back into the scrollable area and account for
            // viewport scroll pos.
            if (rect.top - menuHeight < scrollableRefRect.top) {
              newTop = scrollableRefRect.top + winScrollPos.y + borderPadding;
            }

            // Also keep the menu inside the bottom of the scrollable
            if (newTop > scrollableRefRect.bottom + winScrollPos.y) {
              newTop =
                scrollableRefRect.bottom -
                (menuHeight + borderPadding) +
                winScrollPos.y;
            }
          }

          // Without ref, we keep within the screen
          if (!scrollContainerRef?.current) {
            if (rect.top - menuHeight < 0) {
              newTop = winScrollPos.y + borderPadding;
            }
          }

          const calcStyles: React.CSSProperties = {
            top: `${newTop}px`,
            left: `${newLeft}px`,
            position: 'absolute',
            right: newRight,
          };
          ddt.log('editormenu', 'Menu Position', {
            calcStyles,
            rect,
            scrollableRefRect,
            scrollContainerRef,
            winScrollPos,
          });

          setMenuStyles(calcStyles);

          // Left here because it can be useful to visualise the rect :)
          /* const debugRect = document.getElementById('debug-rect');
          const debug = debugRect ?? document.createElement('div');
          if (!debugRect) {
            document.body.appendChild(debug);
          }

          const styles: React.CSSProperties = {
            position: 'absolute',
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            left: `${rect.left + window.scrollX}px`,
            top: `${rect.top + window.scrollY}px`,
          };

          debug.setAttribute('id', 'debug-rect');
          debug.style.position = 'absolute';
          debug.style.backgroundColor = '#ff0000aa';
          debug.style.width = `${rect.width}px`;
          debug.style.height = `${rect.height}px`;
          debug.style.left = styles.left as any;
          debug.style.top = styles.top as any;
          debug.style.zIndex = '999999';
          debug.style.minWidth = '10px';
          debug.style.pointerEvents = 'none';

          // Left here because it can be useful to visualise the rect :)
          const debugRect2 = document.getElementById('debug-rect2');
          const debug2 = debugRect2 ?? document.createElement('div');
          if (!debugRect2) {
            document.body.appendChild(debug2);
          }

          const styles2: React.CSSProperties = {
            position: 'absolute',
            width: `${scrollableRefRect?.width}px`,
            height: `${scrollableRefRect?.height}px`,
            left: `${(scrollableRefRect?.left ?? 0) + window.scrollX}px`,
            top: `${(scrollableRefRect?.top ?? 0) + winScrollPos.y}px`,
          };

          debug2.setAttribute('id', 'debug-rect2');
          debug2.style.position = 'absolute';
          debug2.style.backgroundColor = '#00ff00aa';
          debug2.style.width = `${scrollableRefRect?.width}px`;
          debug2.style.height = `${scrollableRefRect?.height}px`;
          debug2.style.left = styles2.left as any;
          debug2.style.top = styles2.top as any;
          debug2.style.zIndex = '999999';
          debug2.style.minWidth = '10px';
          debug2.style.pointerEvents = 'none';*/
        }
      });
    }
  }, [
    editor.state.selection,
    editor.view,
    open,
    winScrollPos,
    innerScrollPos,
    menuOffset,
    menuHeight,
    scrollContainerRef,
    menuWidth,
    borderPadding,
    childSelector,
    menuUp,
  ]);

  if (!open) {
    return null;
  }

  return (
    <ContainerPortal>
      <div className={cn(s['wrapper'], className)} style={menuStyles}>
        {children}
      </div>
    </ContainerPortal>
  );
};
BlockBubbleMenu.displayName = 'BlockBubbleMenu';

export default BlockBubbleMenu;
